import { FC, HTMLAttributes } from "react";
import Image from "next/image";
import clsx from "clsx";

import HtmlText from "@/sections/common/HtmlText";
import CTAButton from "@/components/atoms/buttons/CTAButton";
import { ITextSectionContent } from "./TextSection";

// Three-tier button rule applied across both layout modes:
//   idx 0 → orange   (primary conversion CTA)
//   idx 1 → primary  (secondary blue)
//   idx 2+→ outline-brand (tertiary bordered blue)
const variantFor = (idx: number) =>
  idx === 0 ? "orange" : idx === 1 ? "primary" : "outline-brand";

// ----------------------------------------------------------------------------
// Text + image-grid section. Two layout modes:
//
//   isTwoColumnSection=true  → text on the LEFT, image grid on the RIGHT
//     inside a tinted card-style band. Used on the home page for the
//     "Identified vulnerabilities, remediated loopholes" trust block.
//
//   isTwoColumnSection=false → heading/description across the top, images
//     in a centered single row below. Used by team-contribution sections
//     on other pages.
//
// Polished to use the canonical Bricolage Grotesque + clamp typography
// and light font weight that the rest of the redesigned home sections
// use, with orange CTAButton replacing the plain TButton.
// ----------------------------------------------------------------------------

export type ITextWithImageGridProps = HTMLAttributes<HTMLDivElement> &
  ITextSectionContent & {
    images?: { src: string; alt: string }[];
    imageClass?: string;
    isTwoColumnSection?: boolean;
  };

const TextWithImageGrid: FC<ITextWithImageGridProps> = ({
  heading,
  description,
  buttons,
  images,
  isTwoColumnSection = true,
  className,
  imageClass,
  ...other
}) => {
  // ---- TWO-COLUMN MODE — home page trust block ----
  if (isTwoColumnSection) {
    return (
      <section
        className={clsx(
          "py-10 md:py-14 lg:py-16 xl:py-20",
          className,
        )}
        {...other}
      >
        <div className="container">
          <div className="rounded-2xl bg-primary-50 p-8 md:p-10 lg:p-14">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
              {/* LEFT — text */}
              <div className="text-center lg:col-span-5 lg:text-left">
                <p className="mb-3 font-bricolage-grotesque text-[clamp(0.75rem,0.4vw+0.65rem,0.875rem)] font-semibold uppercase tracking-[0.25em] text-primary-900">
                  Vulnerabilities disclosed to
                </p>
                {heading && (
                  <h2 className="font-bricolage-grotesque text-[clamp(1.375rem,2.5vw+0.75rem,2.5rem)] font-extralight !leading-[1.15] tracking-tight text-black-950">
                    {heading}
                  </h2>
                )}
                {description && (
                  <div className="mt-4 text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] font-extralight leading-relaxed text-black-700">
                    <HtmlText className="prose-p:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] prose-p:!font-extralight prose-p:!text-black-700">
                      {description}
                    </HtmlText>
                  </div>
                )}
                {buttons && buttons.length > 0 && (
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                    {buttons.map((b, idx) => (
                      <CTAButton
                        key={b.id ?? idx}
                        id={b.id}
                        href={b.link}
                        target={b.target}
                        variant={variantFor(idx)}
                        radius="full"
                        size="md"
                        showIcon
                        iconType="arrow"
                        iconPosition="right"
                      >
                        {b.text}
                      </CTAButton>
                    ))}
                  </div>
                )}
              </div>

              {/* RIGHT — logo grid */}
              {images && images.length > 0 && (
                <div className="lg:col-span-7">
                  <div className="grid grid-cols-3 items-center justify-items-center gap-x-6 gap-y-8 sm:grid-cols-4 md:gap-x-8 md:gap-y-10">
                    {images.map(({ src, alt }, idx) => (
                      <div
                        key={idx}
                        className="flex h-14 w-full items-center justify-center md:h-16 lg:h-20"
                      >
                        <Image
                          src={src}
                          alt={alt}
                          height={80}
                          width={140}
                          className={clsx(
                            "h-full w-auto object-contain opacity-80 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0",
                            imageClass,
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---- SINGLE-COLUMN MODE — team-contribution layout (other pages) ----
  return (
    <section
      className={clsx("bg-primary-50 py-10 md:py-14 lg:py-16", className)}
      {...other}
    >
      <div className="container">
        {(heading || description) && (
          <div className="mx-auto max-w-3xl text-center">
            {heading && (
              <h2 className="font-bricolage-grotesque text-[clamp(1.375rem,2.5vw+0.75rem,2.5rem)] font-extralight !leading-[1.15] tracking-tight text-black-950">
                {heading}
              </h2>
            )}
            {description && (
              <div className="mt-4 text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] font-extralight leading-relaxed text-black-700">
                <HtmlText className="prose-p:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)] prose-p:!font-extralight prose-p:!text-black-700">
                  {description}
                </HtmlText>
              </div>
            )}
            {buttons && buttons.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                {buttons.map((b, idx) => (
                  <CTAButton
                    key={b.id ?? idx}
                    id={b.id}
                    href={b.link}
                    target={b.target}
                    variant={variantFor(idx)}
                    radius="full"
                    size="md"
                    showIcon
                    iconType="arrow"
                    iconPosition="right"
                  >
                    {b.text}
                  </CTAButton>
                ))}
              </div>
            )}
          </div>
        )}

        {images && images.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-12 gap-y-8 md:mt-12 md:gap-x-16 lg:gap-x-20">
            {images.map(({ src, alt }, idx) => (
              <div
                key={idx}
                className="flex h-14 items-center md:h-16 lg:h-20"
              >
                <Image
                  src={src}
                  alt={alt}
                  height={80}
                  width={140}
                  className={clsx(
                    "h-full w-auto object-contain opacity-80 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0",
                    imageClass,
                  )}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TextWithImageGrid;
